import React, { useState, useEffect } from 'react';
import { crmApi, tagsApi } from '../services/crmApi';
import { Clock, RefreshCw, User, ArrowRight } from 'lucide-react';

interface TimelineEntry {
  id: number;
  username?: string;
  changed_by_name?: string;
  action?: string;
  details?: string;
  from_stage?: string;
  to_stage?: string;
  from_stage_label?: string;
  to_stage_label?: string;
  change_reason?: string;
  created_at: string;
}

export const StatusTimeline: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadTimeline();
  }, [applicationId]);

  const loadTimeline = async () => {
    try {
      // Try new stage history first, fall back to audit_logs
      const [stageData, auditData] = await Promise.all([
        tagsApi.getStageHistory(applicationId).catch(err => { console.warn('[StatusTimeline] stage history fetch:', err.message); return []; }),
        crmApi.getDashboardTimeline(applicationId).catch(err => { console.warn('[StatusTimeline] timeline fetch:', err.message); return { timeline: [] }; }),
      ]);

      // Merge: stage history entries + audit log entries, deduplicate by timestamp proximity
      const stageEntries: TimelineEntry[] = (stageData || []).map((s: any) => ({
        id: s.id,
        changed_by_name: s.changed_by_name,
        from_stage: s.from_stage,
        to_stage: s.to_stage,
        from_stage_label: s.from_stage_label,
        to_stage_label: s.to_stage_label,
        change_reason: s.change_reason,
        created_at: s.created_at,
        _source: 'stage',
      }));

      const auditEntries: TimelineEntry[] = (auditData.timeline || []).map((a: any) => ({
        id: a.id + 100000,
        username: a.username,
        action: a.action,
        details: a.details,
        created_at: a.created_at,
        _source: 'audit',
      }));

      // If we have stage history, prefer it for status changes
      // Keep audit entries that are NOT status changes (other actions)
      let merged: TimelineEntry[];
      if (stageEntries.length > 0) {
        const nonStatusAudits = auditEntries.filter((a: any) => a.action !== 'STATUS_CHANGE');
        merged = [...stageEntries, ...nonStatusAudits];
      } else {
        merged = auditEntries;
      }

      // Sort by date descending
      merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setTimeline(merged);
    } catch (error) {
      console.error('Timeline load error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4 flex items-center">
          <Clock className="mr-2 text-blue-600" size={18} />
          Historia zmian
        </h3>
        <div className="flex items-center justify-center py-4">
          <RefreshCw className="animate-spin text-gray-400" size={18} />
        </div>
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4 flex items-center">
          <Clock className="mr-2 text-blue-600" size={18} />
          Historia zmian
        </h3>
        <p className="text-sm text-gray-400 text-center py-2">Brak zmian w historii</p>
      </div>
    );
  }

  const displayed = expanded ? timeline : timeline.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-bold mb-4 flex items-center">
        <Clock className="mr-2 text-blue-600" size={18} />
        Historia zmian ({timeline.length})
      </h3>
      <div className="space-y-0">
        {displayed.map((entry, i) => {
          const date = new Date(entry.created_at);
          const isLast = i === displayed.length - 1;
          const isStageEntry = !!(entry as any)._source === true || !!entry.to_stage;

          return (
            <div key={entry.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${isStageEntry ? 'bg-blue-500' : 'bg-gray-400'}`} />
                {!isLast && <div className="w-0.5 flex-1 bg-gray-200" />}
              </div>

              <div className="flex-1 pb-4">
                <div className="text-sm font-medium text-gray-800">
                  {entry.to_stage ? (
                    <span className="flex items-center gap-1 flex-wrap">
                      Zmiana statusu:
                      {entry.from_stage_label && (
                        <>
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs">
                            {entry.from_stage_label}
                          </span>
                          <ArrowRight size={12} className="text-gray-400" />
                        </>
                      )}
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">
                        {entry.to_stage_label || entry.to_stage}
                      </span>
                    </span>
                  ) : (
                    formatAction(entry.action || '', entry.details)
                  )}
                </div>
                {entry.change_reason && (
                  <p className="text-xs text-gray-500 mt-0.5 italic">Powód: {entry.change_reason}</p>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={12} className="text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {entry.changed_by_name || entry.username || 'System'}
                  </span>
                  <span className="text-xs text-gray-300">|</span>
                  <span className="text-xs text-gray-400">
                    {date.toLocaleDateString('pl-PL')} {date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {timeline.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full text-center text-xs text-blue-600 hover:text-blue-800 mt-2"
          type="button"
        >
          {expanded ? 'Pokaż mniej' : `Pokaż wszystkie (${timeline.length})`}
        </button>
      )}
    </div>
  );
};

function formatAction(action: string, detailsStr?: string): React.ReactNode {
  let details: any = {};
  try { details = JSON.parse(detailsStr || '{}'); } catch { /* ignore */ }

  if (action === 'STATUS_CHANGE' && details.old_status && details.new_status) {
    return (
      <span className="flex items-center gap-1 flex-wrap">
        Zmiana statusu:
        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs">{details.old_status}</span>
        <ArrowRight size={12} className="text-gray-400" />
        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">{details.new_status}</span>
      </span>
    );
  }
  return action;
}
